import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState, useMemo } from 'react'
import { Pencil, Trash2, Plus, ChevronsUpDown, Check, Minus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { Checkbox } from '@/components/ui/checkbox'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminFieldLibrary } from '../hooks/useAdminFieldLibrary'
import { FIELD_TYPES } from '../components/FormBuilder/FieldTypePicker'
import type { FieldLibraryDocument, ApplicationFieldType } from '@/types'
import type { FieldLibraryFormValues } from '../types'

const FILE_TYPE_CATEGORIES = [
  {
    label: 'Images',
    types: [
      { label: 'JPEG', value: 'image/jpeg' },
      { label: 'PNG', value: 'image/png' },
      { label: 'GIF', value: 'image/gif' },
      { label: 'WebP', value: 'image/webp' },
      { label: 'SVG', value: 'image/svg+xml' },
    ],
  },
  {
    label: 'Documents',
    types: [
      { label: 'PDF', value: 'application/pdf' },
      { label: 'Word (.doc)', value: '.doc' },
      { label: 'Word (.docx)', value: '.docx' },
      { label: 'Plain Text', value: 'text/plain' },
      { label: 'RTF', value: '.rtf' },
    ],
  },
  {
    label: 'Spreadsheets',
    types: [
      { label: 'Excel (.xls)', value: '.xls' },
      { label: 'Excel (.xlsx)', value: '.xlsx' },
      { label: 'CSV', value: 'text/csv' },
    ],
  },
  {
    label: 'Presentations',
    types: [
      { label: 'PowerPoint (.ppt)', value: '.ppt' },
      { label: 'PowerPoint (.pptx)', value: '.pptx' },
    ],
  },
  {
    label: 'Video',
    types: [
      { label: 'MP4', value: 'video/mp4' },
      { label: 'WebM', value: 'video/webm' },
      { label: 'MOV', value: 'video/quicktime' },
      { label: 'AVI', value: 'video/x-msvideo' },
    ],
  },
  {
    label: 'Audio',
    types: [
      { label: 'MP3', value: 'audio/mpeg' },
      { label: 'WAV', value: 'audio/wav' },
      { label: 'OGG', value: 'audio/ogg' },
      { label: 'M4A', value: 'audio/mp4' },
    ],
  },
  {
    label: 'Archives',
    types: [
      { label: 'ZIP', value: 'application/zip' },
      { label: 'RAR', value: 'application/x-rar-compressed' },
      { label: '7-Zip', value: 'application/x-7z-compressed' },
    ],
  },
] as const

const EMPTY_FORM: FieldLibraryFormValues = {
  fieldId: '',
  type: 'short_text',
  label: '',
  description: '',
  placeholder: '',
  category: 'General',
  options: [],
  min: '',
  max: '',
  minLabel: '',
  maxLabel: '',
  maxFileSizeMB: '',
  acceptedFileTypes: [],
  maxEntries: '',
}

export function AdminFieldLibraryPage() {
  const { fields, loading, error, createField, updateField, deleteField } = useAdminFieldLibrary()
  const existingCategories = useMemo(
    () => Array.from(new Set(fields.map((f) => f.category).filter(Boolean) as string[])).sort(),
    [fields],
  )
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<(FieldLibraryDocument & { id: string }) | null>(null)
  const [formValues, setFormValues] = useState<FieldLibraryFormValues>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<(FieldLibraryDocument & { id: string }) | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  function openAdd() {
    setEditTarget(null)
    setFormValues(EMPTY_FORM)
    setFormError(null)
    setFormOpen(true)
  }

  function openEdit(field: FieldLibraryDocument & { id: string }) {
    setEditTarget(field)
    setFormValues({
      fieldId: field.fieldId,
      type: field.type,
      label: field.label,
      description: field.description ?? '',
      placeholder: field.placeholder ?? '',
      category: field.category ?? 'General',
      options: field.options ?? [],
      min: field.min ?? '',
      max: field.max ?? '',
      integerOnly: field.integerOnly ?? false,
      minLabel: field.minLabel ?? '',
      maxLabel: field.maxLabel ?? '',
      maxFileSizeMB: field.maxFileSizeMB ?? '',
      acceptedFileTypes: field.acceptedFileTypes ?? [],
      maxEntries: field.maxEntries ?? '',
    })
    setFormError(null)
    setFormOpen(true)
  }

  async function handleSave() {
    if (!formValues.fieldId || !formValues.label) {
      setFormError(withErrorCode('Field ID and Label are required.', 'app/validation'))
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editTarget) {
        await updateField(editTarget.id, formValues)
      } else {
        await createField(formValues)
      }
      setFormOpen(false)
    } catch (err) {
      setFormError(formatUserFacingError(err, 'Failed to save field'))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteField(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDeleteOpen(false)
    }
  }

  return (
    <AdminListPage
      title="Field Library"
      action={
        <Button onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          New Field
        </Button>
      }
      error={error}
      loading={loading}
      skeletonRows={5}
      dialog={
        <>
          {/* Edit / Create dialog */}
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger className="hidden" />
            <DialogContent className="max-w-md flex flex-col">
              <DialogHeader>
                <DialogTitle>{editTarget ? 'Edit Library Field' : 'New Library Field'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto max-h-[65vh] pr-1">
                <div className="space-y-1">
                  <Label>Field ID</Label>
                  <Input
                    className="font-mono"
                    placeholder="applicant-email"
                    value={formValues.fieldId}
                    onChange={(e) => setFormValues((v) => ({ ...v, fieldId: e.target.value }))}
                    disabled={!!editTarget}
                  />
                  <p className="text-xs text-muted-foreground">Lowercase letters, numbers, hyphens only. Cannot be changed.</p>
                </div>
                <div className="space-y-1">
                  <Label>Field Type</Label>
                  <Select
                    value={formValues.type}
                    onValueChange={(v) => setFormValues((f) => ({ ...f, type: v as ApplicationFieldType }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((t) => (
                        <SelectItem key={t.type} value={t.type}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Label</Label>
                  <Input
                    placeholder="e.g. Applicant Email"
                    value={formValues.label}
                    onChange={(e) => setFormValues((v) => ({ ...v, label: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Popover open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={categoryPopoverOpen}
                        className="w-full justify-between font-normal"
                      >
                        {formValues.category || 'Select or create a category…'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput
                          placeholder="Search or type new category…"
                          value={formValues.category}
                          onValueChange={(v) => setFormValues((f) => ({ ...f, category: v }))}
                        />
                        <CommandList>
                          <CommandEmpty>
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm"
                              onClick={() => setCategoryPopoverOpen(false)}
                            >
                              Create &ldquo;{formValues.category}&rdquo;
                            </button>
                          </CommandEmpty>
                          <CommandGroup>
                            {existingCategories.map((cat) => (
                              <CommandItem
                                key={cat}
                                value={cat}
                                onSelect={(v) => {
                                  setFormValues((f) => ({ ...f, category: v }))
                                  setCategoryPopoverOpen(false)
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${formValues.category === cat ? 'opacity-100' : 'opacity-0'}`}
                                />
                                {cat}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label>Description (optional)</Label>
                  <Input
                    value={formValues.description}
                    onChange={(e) => setFormValues((v) => ({ ...v, description: e.target.value }))}
                  />
                </div>
                {(formValues.type === 'dropdown' ||
                  formValues.type === 'multiple_choice' ||
                  formValues.type === 'checkbox_group') && (
                  <div className="space-y-2">
                    <Label>Options</Label>
                    <div className="space-y-1.5">
                      {formValues.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            value={opt}
                            placeholder={`Option ${i + 1}`}
                            onChange={(e) =>
                              setFormValues((v) => {
                                const options = [...v.options]
                                options[i] = e.target.value
                                return { ...v, options }
                              })
                            }
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() =>
                              setFormValues((v) => ({
                                ...v,
                                options: v.options.filter((_, j) => j !== i),
                              }))
                            }
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() =>
                        setFormValues((v) => ({ ...v, options: [...v.options, ''] }))
                      }
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Option
                    </Button>
                  </div>
                )}
                {['linear_scale', 'number'].includes(formValues.type) && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>Min</Label>
                        <Input
                          type="number"
                          placeholder="1"
                          value={formValues.min}
                          onChange={(e) =>
                            setFormValues((v) => ({
                              ...v,
                              min: e.target.value === '' ? '' : Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Max</Label>
                        <Input
                          type="number"
                          placeholder="5"
                          value={formValues.max}
                          onChange={(e) =>
                            setFormValues((v) => ({
                              ...v,
                              max: e.target.value === '' ? '' : Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                    </div>
                    {formValues.type === 'linear_scale' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>
                          Min Label{' '}
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          placeholder="e.g. Not at all"
                          value={formValues.minLabel}
                          onChange={(e) =>
                            setFormValues((v) => ({ ...v, minLabel: e.target.value }))
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>
                          Max Label{' '}
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          placeholder="e.g. Extremely"
                          value={formValues.maxLabel}
                          onChange={(e) =>
                            setFormValues((v) => ({ ...v, maxLabel: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    )}
                  </>
                )}
                {formValues.type === 'number' && (
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" checked={formValues.integerOnly ?? false}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, integerOnly: e.target.checked }))} />
                    Whole numbers only
                  </Label>
                )}

                {formValues.type === 'file_upload' && (
                  <>
                    <div className="space-y-1">
                      <Label>Max File Size (MB)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        placeholder="10"
                        value={formValues.maxFileSizeMB}
                        onChange={(e) =>
                          setFormValues((v) => ({
                            ...v,
                            maxFileSizeMB: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Accepted File Types (optional)</Label>
                      <p className="text-xs text-muted-foreground">
                        Select individual types or check a category to select all. Leave all unchecked to accept any file.
                      </p>
                      <div className="rounded-md border divide-y max-h-64 overflow-y-auto">
                        {FILE_TYPE_CATEGORIES.map((cat) => {
                          const catValues = cat.types.map((t) => t.value)
                          const selectedCount = catValues.filter((v) =>
                            formValues.acceptedFileTypes.includes(v)
                          ).length
                          const allSelected = selectedCount === catValues.length
                          const someSelected = selectedCount > 0 && !allSelected

                          function toggleCategory() {
                            setFormValues((prev) => {
                              const without = prev.acceptedFileTypes.filter(
                                (v) => !(catValues as string[]).includes(v)
                              )
                              return {
                                ...prev,
                                acceptedFileTypes: allSelected ? without : [...without, ...catValues],
                              }
                            })
                          }

                          function toggleType(value: string) {
                            setFormValues((prev) => ({
                              ...prev,
                              acceptedFileTypes: prev.acceptedFileTypes.includes(value)
                                ? prev.acceptedFileTypes.filter((v) => v !== value)
                                : [...prev.acceptedFileTypes, value],
                            }))
                          }

                          return (
                            <div key={cat.label}>
                              <button
                                type="button"
                                onClick={toggleCategory}
                                className="flex w-full items-center gap-2 px-3 py-2 bg-muted/40 hover:bg-muted/70 transition-colors"
                              >
                                <div className="h-4 w-4 shrink-0 rounded border border-primary flex items-center justify-center bg-background">
                                  {allSelected && <Check className="h-3 w-3 text-primary" />}
                                  {someSelected && <Minus className="h-3 w-3 text-primary" />}
                                </div>
                                <span className="text-sm font-medium">{cat.label}</span>
                                {selectedCount > 0 && (
                                  <span className="ml-auto text-xs text-muted-foreground">
                                    {selectedCount}/{catValues.length}
                                  </span>
                                )}
                              </button>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-4 py-2">
                                {cat.types.map((type) => (
                                  <label
                                    key={type.value}
                                    className="flex items-center gap-2 cursor-pointer"
                                  >
                                    <Checkbox
                                      checked={formValues.acceptedFileTypes.includes(type.value)}
                                      onCheckedChange={() => toggleType(type.value)}
                                    />
                                    <span className="text-sm">{type.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )}
                {formError && <p className="text-sm text-destructive">{formError}</p>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
                <Button onClick={() => void handleSave()} disabled={saving}>
                  {saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Create Field'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete dialog */}
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogTrigger className="hidden" />
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Library Field</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-foreground">{deleteTarget?.label}</span>? Forms
                using this field will be unaffected, but it will no longer be available to add.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteTarget(null) }}>Cancel</Button>
                <Button variant="destructive" onClick={() => void handleDelete()} disabled={deletingId !== null}>
                  {deletingId !== null ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label</TableHead>
            <TableHead>Field ID</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {fields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  No fields in library yet.
                </TableCell>
              </TableRow>
            ) : (
              fields.map((field) => {
                const typeInfo = FIELD_TYPES.find((t) => t.type === field.type)
                return (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium">{field.label}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {field.fieldId}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{typeInfo?.label ?? field.type}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{field.category ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end">
                        <MultiButton
                          variant="outline"
                          size="sm"
                          items={[
                            {
                              id: 'edit',
                              icon: Pencil,
                              label: 'Edit',
                              onClick: () => openEdit(field),
                            },
                            {
                              id: 'delete',
                              icon: Trash2,
                              label: 'Delete',
                              onClick: () => { setDeleteTarget(field); setDeleteOpen(true) },
                              disabled: deletingId === field.id,
                              className: 'text-destructive hover:text-destructive',
                            },
                          ]}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
